import { FC } from 'react';
import { Link as CarbonLink } from '@carbon/react';
import { format0 } from 'utils/number';
import { entityIdLabel } from 'utils/entity-id';
import type { DataTableColumnDef } from 'components/data-table';
import type { AddressFailure, AddressGroup, AddressScope, Run } from '../types';
import { EXECUTABLE_STATUSES } from './address-group-variations';
import styles from '../page.module.scss';

// The groups a finished reversal couldn't undo, read off the run summary: the
// reversal's step outputs carry a per-batch refused tally, summed onto every
// execution of its clustering run, so the worst (they all match) is the count.
export const refusedFromRuns = (runs: Run[]) =>
  runs.reduce((max, run) => Math.max(max, run.refused ?? 0), 0);

// A group the reviewer can still include or exclude.
export const isToggleable = (g: AddressGroup) =>
  EXECUTABLE_STATUSES.has(g.status) || g.status === 'excluded';

export const EntityCell: FC<{
  id: number;
  name: string | null;
  kind: string | null;
}> = ({ id, name, kind }) => (
  <span>
    <CarbonLink
      href={`/entities/${id}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {name ?? `Entity ${id}`}
    </CarbonLink>
    <span className={styles.entityMetaLine}>
      {entityIdLabel(id)}
      {kind ? ` · ${kind}` : ''}
    </span>
  </span>
);

export const failureColumns: DataTableColumnDef<AddressFailure>[] = [
  {
    id: 'entity',
    header: 'Entity',
    size: 320,
    meta: { wrap: true, align: 'left' },
    cell: ({ row }) => (
      <EntityCell
        id={row.original.entityId}
        name={row.original.entityName}
        kind={row.original.entityKind}
      />
    ),
  },
  {
    accessorKey: 'addresses',
    header: 'Addresses',
    size: 120,
    cell: ({ getValue }) => format0(getValue() as number),
  },
  {
    accessorKey: 'reason',
    header: 'Reason',
    size: 600,
    meta: { wrap: true, align: 'left' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '--',
  },
];

// How many areas the scope line shows before collapsing the rest into
// "+N more"; the full list stays in the title tooltip.
const SCOPE_PREVIEW = 5;

// The run's areas, so a reviewer can see what it covers; an empty scope is
// every location. A wide run (hundreds of counties) would otherwise print one
// long unreadable line, so it shows the first few and "+N more".
export function formatScope(
  scope: AddressScope | null
): { summary: string; full: string } | null {
  if (!scope) return null;
  const parts = scope.areas.map((a) => {
    return a.county ? `${a.county}, ${a.state}` : a.state;
  });
  if (scope.unassigned) parts.push('Unassigned');
  if (parts.length === 0)
    return { summary: 'All locations', full: 'All locations' };
  const full = parts.join(' · ');
  if (parts.length <= SCOPE_PREVIEW) return { summary: full, full };
  const summary = `${parts.slice(0, SCOPE_PREVIEW).join(' · ')} · +${format0(
    parts.length - SCOPE_PREVIEW
  )} more`;
  return { summary, full };
}
